import axios from 'axios';

let loggingInitialized = false;
let routerInstance = null;

const apiBase = process.env.API_DIRECTORY || '';
const logEndpoint = joinUrl(apiBase, 'logs/');
const currentUserEndpoint = joinUrl(apiBase, 'current-user/');
const rawClient = axios.create();
const defaultExcludePatterns = ['/current-user/'];

let excludePatterns = [...defaultExcludePatterns];

let cachedUser = null;
let cachedUserPromise = null;
let logQueue = [];
let flushTimer = null;
let batchSize = 10;
let batchIntervalMs = 5000;

const SENSITIVE_KEYS = [
  'password',
  'token',
  'authorization',
  'auth',
  'authToken',
  'refresh',
  'refresh_token',
  'access_token',
].map((key) => key.toLowerCase());

function joinUrl(base, path) {
  const baseTrimmed = (base || '').replace(/\/+$/, '');
  const pathTrimmed = (path || '').replace(/^\/+/, '');
  if (!baseTrimmed) {
    return `/${pathTrimmed}`;
  }
  return `${baseTrimmed}/${pathTrimmed}`;
}

function matchesExcludePattern(url) {
  if (!url) {
    return false;
  }
  const urlText = String(url);
  return excludePatterns.some((pattern) => {
    if (!pattern) {
      return false;
    }
    if (pattern instanceof RegExp) {
      return pattern.test(urlText);
    }
    return urlText.includes(String(pattern));
  });
}

function shouldSkipLog(url) {
  if (!url) {
    return false;
  }
  return String(url).includes('/logs/') || matchesExcludePattern(url);
}

function redactValue(value) {
  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    return '[FormData]';
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (Array.isArray(value)) {
    return value.map(redactValue);
  }
  if (value && typeof value === 'object') {
    const redacted = {};
    Object.keys(value).forEach((key) => {
      if (SENSITIVE_KEYS.includes(String(key).toLowerCase())) {
        redacted[key] = '[REDACTED]';
      } else {
        redacted[key] = redactValue(value[key]);
      }
    });
    return redacted;
  }
  return value;
}

function getRouteInfo() {
  const route = routerInstance?.currentRoute?.value;
  const matched = route?.matched ? route.matched[route.matched.length - 1] : null;
  const matchedComponent = matched?.components?.default;
  const componentName =
    matchedComponent?.name ||
    matchedComponent?.__name ||
    route?.name ||
    null;
  return {
    route_name: route?.name ?? null,
    route_path: route?.path ?? null,
    component: componentName,
  };
}

function getAuthToken() {
  return localStorage.getItem('authToken');
}

async function ensureCurrentUser() {
  if (cachedUser) {
    return cachedUser;
  }
  if (cachedUserPromise) {
    return cachedUserPromise;
  }

  const token = getAuthToken();
  if (!token) {
    cachedUser = null;
    return null;
  }

  cachedUserPromise = rawClient
    .get(currentUserEndpoint, {
      headers: {
        Authorization: `Token ${token}`,
      },
    })
    .then((response) => {
      cachedUser = response.data;
      if (typeof window !== 'undefined') {
        const permissions = Array.isArray(cachedUser?.permissions) ? cachedUser.permissions : [];
        localStorage.setItem('userPermissions', JSON.stringify(permissions));
      }
      return cachedUser;
    })
    .catch(() => {
      cachedUser = null;
      return null;
    })
    .finally(() => {
      cachedUserPromise = null;
    });

  return cachedUserPromise;
}

async function sendLogBatch(payloads) {
  try {
    const token = getAuthToken();
    await rawClient.post(logEndpoint, { logs: payloads }, {
      headers: token ? { Authorization: `Token ${token}` } : {},
    });
  } catch (error) {
    // Intentionally swallow logging errors to avoid impacting UX.
  }
}

function scheduleFlush() {
  if (flushTimer || batchIntervalMs <= 0) {
    return;
  }
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushLogs();
  }, batchIntervalMs);
}

function flushLogs() {
  if (!logQueue.length) {
    return;
  }
  const batch = logQueue;
  logQueue = [];
  sendLogBatch(batch);
}

function enqueueLog(payload) {
  logQueue.push(payload);
  if (logQueue.length >= batchSize) {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    flushLogs();
    return;
  }
  scheduleFlush();
}

function buildLogPayload({ config, response, error }) {
  const routeInfo = getRouteInfo();
  const user = cachedUser;
  const requestUrl = config?.url ?? null;
  const originUrl = typeof window !== 'undefined' ? window.location.href : null;
  const method = config?.method ? String(config.method).toUpperCase() : null;
  const timestamp = new Date().toISOString();

  const payload = {
    timestamp,
    endpoint: requestUrl,
    method,
    payload: redactValue(config?.data ?? null),
    params: redactValue(config?.params ?? null),
    status_code: response?.status ?? error?.response?.status ?? null,
    response_body: redactValue(response?.data ?? error?.response?.data ?? null),
    origin_url: originUrl,
    user_id: user?.id ?? null,
    username: user?.username ?? null,
    ...routeInfo,
    component: config?.metadata?.component ?? routeInfo.component ?? null,
  };

  return payload;
}

export function initApiLogging({ router, exclude = [], batchSize: nextBatchSize, batchIntervalMs: nextBatchIntervalMs } = {}) {
  if (loggingInitialized) {
    return;
  }
  loggingInitialized = true;
  routerInstance = router || null;
  excludePatterns = Array.isArray(exclude)
    ? [...defaultExcludePatterns, ...exclude]
    : [...defaultExcludePatterns];
  if (typeof nextBatchSize === 'number') {
    batchSize = nextBatchSize;
  }
  if (typeof nextBatchIntervalMs === 'number') {
    batchIntervalMs = nextBatchIntervalMs;
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flushLogs);
  }

  axios.interceptors.request.use((config) => {
    const routeInfo = getRouteInfo();
    const token = getAuthToken();
    if (token) {
      const headers = config.headers || {};
      if (!headers.Authorization && !headers.authorization) {
        headers.Authorization = `Token ${token}`;
        config.headers = headers;
      }
    }
    config.metadata = {
      route_name: routeInfo.route_name,
      route_path: routeInfo.route_path,
      component: config?.metadata?.component ?? null,
    };
    return config;
  });

  axios.interceptors.response.use(
    async (response) => {
      const config = response?.config;
      if (!config || shouldSkipLog(config.url)) {
        return response;
      }
      ensureCurrentUser().then(() => {
        const logPayload = buildLogPayload({ config, response });
        enqueueLog(logPayload);
      });
      return response;
    },
    async (error) => {
      const config = error?.config;
      if (!config || shouldSkipLog(config.url)) {
        return Promise.reject(error);
      }
      ensureCurrentUser().then(() => {
        const logPayload = buildLogPayload({ config, error });
        enqueueLog(logPayload);
      });
      return Promise.reject(error);
    }
  );
}

